import { ExclamationCircleFilled } from '@ant-design/icons'
import { ConfigProvider, Select as AntdSelect, type SelectProps } from 'antd'
import classNames from 'classnames'
import { useContext, useId, useState } from 'react'
import { Controller, FieldErrors } from 'react-hook-form'

import type { OptionType, SelectFormControlType } from '@/types'

import {
  HoverElementContext,
  type HoverElementContextType,
} from '../HoverElement/HoverElement'
import cn from './AtomSelect.module.scss'

const MULTIPLE_SELECT_THEME = {
  components: {
    Select: {
      controlHeight: 32,
      multipleItemHeight: 24,
      optionHeight: 32,
      optionPadding: '5px 12px',
    },
  },
}

type SelectPropsType = {
  openByHover?: boolean
  options?: OptionType[]
  title?: React.ReactNode
  formControl?: SelectFormControlType
  formName?:
    | 'brand'
    | 'column'
    | 'countryOfOrigin'
    | 'unitOfMeasurement'
    | 'currency'
    | 'paymentType'
    | 'supervisorManager'
    | 'mainBankAccount'
    | 'roles'
    | 'baseSegment'
    | 'valueType'
    | 'supplier'
    | 'stores'
    | 'supplierId'
    | 'storeId'
    | 'sourceType'
    | 'fileFormat'
    | 'cronExpression'
    | 'sheetSelectionType'
    | 'allowedBrands'
    | 'ignoredBrands'
    | 'fallbackBrandId'
  formErrors?: FieldErrors
  error?: string
  showSearch?: boolean
  allowClear?: boolean
  placeholder?: string
  searchValue?: string
  onSearch?: (value: string) => void
  filterOption?: SelectProps['filterOption']
  optionRender?: SelectProps['optionRender']
  popupRender?: SelectProps['popupRender']
  labelRender?: SelectProps['labelRender']
  popupMatchSelectWidth?: SelectProps['popupMatchSelectWidth']
  notFoundContent?: React.ReactNode
  required?: boolean
  value?: OptionType | string | string[] | (() => string)
  children?: React.ReactNode
  disabled?: boolean
  loading?: boolean
  handleChange?: (
    _: OptionType | null | string | (() => string),
    option: OptionType | OptionType[]
  ) => void
  onValuesChange?: (values: string[]) => void
  handleClear?: () => void
  onPopupScroll?: (e: React.UIEvent<HTMLDivElement, UIEvent>) => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  popupClassName?: string
  fullWidth?: boolean
  outlined?: boolean
  mode?: 'multiple'
  maxTagCount?: number | 'responsive'
  maxTagPlaceholder?: SelectProps['maxTagPlaceholder']
  isOrganisationSelected?: boolean
  layout?: 'row' | 'stacked'
  size?: 'default' | 'large'
  valueMode?: 'option' | 'value'
  onChange?: (
    value:
      | OptionType
      | OptionType[]
      | null
      | string
      | string[]
      | (() => string),
    option: OptionType | OptionType[]
  ) => void
}

function AtomSelect(props: SelectPropsType) {
  const {
    options,
    openByHover = true,
    title,
    formControl,
    formName,
    formErrors,
    error,
    showSearch,
    allowClear = showSearch,
    placeholder,
    searchValue,
    onSearch,
    filterOption,
    optionRender,
    popupRender,
    labelRender,
    popupMatchSelectWidth,
    notFoundContent,
    required,
    value,
    children,
    disabled,
    loading,
    handleChange,
    handleClear,
    onPopupScroll,
    open: controlledOpen,
    onOpenChange,
    popupClassName,
    fullWidth = false,
    outlined = false,
    mode,
    maxTagCount,
    maxTagPlaceholder,
    isOrganisationSelected = false,
    layout = 'row',
    size = 'default',
    valueMode = 'option',
  } = props

  const isStacked = layout === 'stacked'
  const plainSelectId = useId()
  const formErrorText =
    formErrors && formName && formErrors[formName]
      ? String(formErrors[formName].message)
      : ''

  const isMultiple = mode === 'multiple'

  const {
    open,
    containerRef,
    setOpen,
    handleMouseEnter,
    handleMouseLeaveContainer,
    handleSelectBlur,
  } = useContext(HoverElementContext) as HoverElementContextType

  const [openSelect, setOpenSelect] = useState<boolean>(false)

  const closeSelect = () => {
    if (openByHover) {
      setOpen(false)
    } else {
      setOpenSelect(false)
      onOpenChange?.(false)
    }
  }

  const handleOpenChange = (visible: boolean) => {
    if (openByHover) return
    setOpenSelect(visible)
    onOpenChange?.(visible)
  }

  const isOpen = openByHover ? open : (controlledOpen ?? openSelect)
  const popupRootClassName =
    popupClassName ??
    (isOrganisationSelected ? cn.selectPopupSelected : cn.selectPopup)

  const plainSelect = (
    <div
      className={classNames(
        cn.select,
        outlined && cn.outlined,
        size === 'large' && cn.large,
        isMultiple && cn.multiple,
        isOrganisationSelected && cn.accentSelected
      )}
    >
      <div
        ref={openByHover ? containerRef : undefined}
        onMouseLeave={openByHover ? handleMouseLeaveContainer : undefined}
      >
        <AntdSelect
          id={title ? plainSelectId : undefined}
          virtual
          options={options && options}
          placeholder={placeholder}
          searchValue={searchValue}
          onSearch={onSearch}
          filterOption={filterOption}
          optionRender={optionRender}
          popupRender={popupRender}
          labelRender={labelRender}
          popupMatchSelectWidth={popupMatchSelectWidth}
          showSearch={showSearch}
          optionFilterProp="label"
          loading={loading}
          onPopupScroll={onPopupScroll}
          classNames={{
            popup: {
              root: popupRootClassName,
            },
          }}
          notFoundContent={
            notFoundContent ?? (
              <div className={cn.selectNotFoundContent}>
                <span>Ничего не найдено</span>
              </div>
            )
          }
          allowClear={allowClear}
          onOpenChange={handleOpenChange}
          open={isOpen}
          mode={mode}
          maxTagCount={maxTagCount}
          maxTagPlaceholder={maxTagPlaceholder}
          onChange={(value, option) => {
            if (isMultiple) {
              if (Array.isArray(value)) props.onValuesChange?.(value)
              return
            }
            if (handleChange && option && !Array.isArray(value)) {
              handleChange(value, option)
            }
            closeSelect()
          }}
          value={value}
          onMouseEnter={openByHover ? handleMouseEnter : undefined}
          onBlur={openByHover ? handleSelectBlur : undefined}
          onClear={() => {
            if (handleClear) {
              handleClear()
            }
            closeSelect()
          }}
        >
          {children}
        </AntdSelect>
      </div>
    </div>
  )

  return (
    <>
      {formControl && formName ? (
        <div
          className={classNames(
            cn.selectItem,
            fullWidth && cn.selectItemFullWidth,
            isStacked && cn.stacked
          )}
        >
          {title && (
            <label htmlFor={`select-${formName}`}>
              {title}
              {isStacked && required && (
                <span className={cn.labelRequired} aria-hidden>
                  *
                </span>
              )}
            </label>
          )}
          <div
            className={classNames(
              cn.select,
              cn.inForm,
              fullWidth && cn.inFormFullWidth,
              isMultiple && cn.multiple,
              formErrors && formErrors[formName] && cn.invalid,
              isOrganisationSelected && cn.accentSelected
            )}
          >
            <div
              ref={openByHover ? containerRef : undefined}
              onMouseLeave={openByHover ? handleMouseLeaveContainer : undefined}
            >
              <ConfigProvider
                theme={isMultiple ? MULTIPLE_SELECT_THEME : undefined}
              >
                <Controller
                  name={formName}
                  control={formControl}
                  render={({ field }) => (
                    <AntdSelect
                      virtual
                      {...field}
                      id={`select-${formName}`}
                      options={options}
                      placeholder={placeholder}
                      optionFilterProp="label"
                      loading={loading}
                      onPopupScroll={onPopupScroll}
                      showSearch={showSearch}
                      optionRender={optionRender}
                      classNames={{
                        popup: {
                          root: popupRootClassName,
                        },
                      }}
                      onOpenChange={handleOpenChange}
                      notFoundContent={
                        notFoundContent ?? (
                          <div className={cn.selectNotFoundContent}>
                            <span>Ничего не найдено</span>
                          </div>
                        )
                      }
                      allowClear={allowClear}
                      open={isOpen}
                      disabled={disabled}
                      mode={mode}
                      maxTagCount={maxTagCount}
                      maxTagPlaceholder={maxTagPlaceholder}
                      onChange={(value, option) => {
                        field.onChange(valueMode === 'value' ? value : option)
                        if (props.onChange) {
                          props.onChange(
                            value,
                            option as OptionType | OptionType[]
                          )
                        }
                        if (!isMultiple) closeSelect()
                      }}
                      onMouseEnter={openByHover ? handleMouseEnter : undefined}
                      onBlur={openByHover ? handleSelectBlur : undefined}
                      onClear={() => {
                        field.onChange(
                          valueMode === 'value'
                            ? isMultiple
                              ? []
                              : undefined
                            : ''
                        )
                        closeSelect()
                      }}
                    />
                  )}
                />
              </ConfigProvider>
              {required && !isStacked && (
                <span className={cn.requiredMark}>*</span>
              )}
            </div>
            <span
              role={isStacked ? 'alert' : undefined}
              title={isStacked && formErrorText ? formErrorText : undefined}
              className={
                formErrorText
                  ? cn.formError
                  : [cn.formError, cn.hidden].join(' ')
              }
            >
              {isStacked && formErrorText && (
                <ExclamationCircleFilled className={cn.errorIcon} />
              )}
              {isStacked ? formErrorText : formErrorText || 'нет'}
            </span>
          </div>
        </div>
      ) : title ? (
        <div
          className={classNames(
            cn.selectItem,
            cn.selectItemFullWidth,
            isStacked && cn.stacked
          )}
        >
          <label htmlFor={plainSelectId}>
            {title}
            {isStacked && required && (
              <span className={cn.labelRequired} aria-hidden>
                *
              </span>
            )}
          </label>
          {plainSelect}
          {isStacked && error !== undefined && (
            <span
              role="alert"
              className={classNames(cn.formError, !error && cn.hidden)}
            >
              {error && <ExclamationCircleFilled className={cn.errorIcon} />}
              {error}
            </span>
          )}
        </div>
      ) : (
        plainSelect
      )}
    </>
  )
}

export default AtomSelect
